import { useState, useEffect, useMemo, useRef } from 'react';
import './AdminChat.css';
import { SERVER_URL } from './config';

export default function AdminChat() {
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const scrollerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${SERVER_URL}chat-sessions`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        const sorted = data.sort(
          (a, b) => new Date(b.last_message_time) - new Date(a.last_message_time)
        );
        setChatSessions(sorted);
      });
  }, []);

  const openChat = (phone) => {
    setSelectedPhone(phone);
    const token = localStorage.getItem('token');
    fetch(`${SERVER_URL}messages?phone=${encodeURIComponent(phone)}`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then(res => res.json())
      .then(data => setMessages(Array.isArray(data) ? data : []));
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !selectedPhone) return;
    const token = localStorage.getItem('token');
    fetch(`${SERVER_URL}send-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
      },
      body: JSON.stringify({ phone: selectedPhone, text: newMessage }),
    })
      .then(res => res.json())
      .then(sent => {
        setMessages(prev => [...prev, sent]);
        setNewMessage('');
      });
  };

  const isOutgoing = (msg) => {
    if (typeof msg.from_me === 'boolean') return msg.from_me;
    if (typeof msg.from_bot === 'boolean') return msg.from_bot;
    if (typeof msg.outgoing === 'boolean') return msg.outgoing;
    if (msg.direction) return String(msg.direction).toLowerCase() === 'outgoing';
    if (msg.sender) return String(msg.sender).toLowerCase() !== 'user';
    return false;
  };

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, selectedPhone]);

  const statusDot = (status) =>
    status === 'human' ? 'var(--status-green)' : 'var(--status-gray)';

  const selectedSession = useMemo(
    () => chatSessions.find(s => s.phone === selectedPhone),
    [chatSessions, selectedPhone]
  );

  return (
    <div className="admin-chat-container">
      <aside className="sidebar">
        <h2>שיחות פעילות</h2>
        <div className="phones-list">
          {chatSessions.map(session => (
            <button
              key={session.phone}
              className={`phone-item ${selectedPhone === session.phone ? 'active' : ''}`}
              onClick={() => openChat(session.phone)}
              title={session.phone}
            >
              <span
                className="status-dot"
                style={{ backgroundColor: statusDot(session.status) }}
              />
              <span className="phone-number">{session.phone}</span>
              {session.unread_count > 0 && (
                <span className="badge">{session.unread_count}</span>
              )}
            </button>
          ))}
        </div>
      </aside>
      <main className="chat-window">
        {selectedPhone ? (
          <>
            <header className="chat-header">
              <div className="chat-title">
                שיחה עם <span className="phone-chip">{selectedPhone}</span>
                {selectedSession?.status && (
                  <span
                    className="status-chip"
                    style={{ backgroundColor: statusDot(selectedSession.status) }}
                  />
                )}
              </div>
            </header>
            <div className="messages" ref={scrollerRef}>
              {messages.map((msg, idx) => {
                const outgoing = isOutgoing(msg);
                return (
                  <div
                    key={idx}
                    className={`message-row ${outgoing ? 'outgoing' : 'incoming'}`}
                  >
                    <div className={`message-bubble ${outgoing ? 'bubble-out' : 'bubble-in'}`}>
                      {msg.text}
                      {msg.timestamp && (
                        <div className="msg-meta">
                          {new Date(msg.timestamp).toLocaleString('he-IL')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="message-input">
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder="הקלד/י הודעה…"
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              />
              <button onClick={sendMessage}>שלח</button>
            </div>
          </>
        ) : (
          <div className="placeholder">בחר/י שיחה מהצד הימני</div>
        )}
      </main>
    </div>
  );
}
